/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React from 'react';
import PropTypes from 'prop-types';

import { IntlProvider } from '@quarry/intl';
import { ErrorBoundary } from 'react-error-boundary';
import ProviderRS2 from '@react/react-spectrum/Provider';

import ConfigContextProvider from '../ConfigContext/ConfigContext';
import MaintenanceMessage from '../MaintenanceMessage/MaintenanceMessage';
import GraphqlSchemaContextProvider from '../GraphqlSchemaContext/GraphqlSchemaContext';
import * as messages from '../../__localization__';

import classes from './Root.scss';
import useLocale from '../common/useLocale';

const withRoot = WrappedComponent => {
    const Root = props => {
        const { configs, ...otherProps } = props;

        const locale = useLocale();

        return (
            <ProviderRS2 theme="light" className={classes.spectrum_provider}>
                <IntlProvider locale={locale} messages={messages}>
                    <ErrorBoundary FallbackComponent={MaintenanceMessage}>
                        <ConfigContextProvider configs={configs}>
                            <GraphqlSchemaContextProvider>
                                <WrappedComponent {...otherProps} />
                            </GraphqlSchemaContextProvider>
                        </ConfigContextProvider>
                    </ErrorBoundary>
                </IntlProvider>
            </ProviderRS2>
        );
    };

    Root.propTypes = {
        configs: PropTypes.arrayOf(PropTypes.object)
    };

    return Root;
};

export default withRoot;
